import supabase from '@/etc/supabase'
import { LOG } from '@/etc/log'
import {
  getElementSections,
  getElementSortIndex,
  groupElementsByKind,
  inferFileExtension,
  kindSupportsOwnerLink,
  normalizeAiElements,
  normalizeElement,
  normalizeElementSection,
  toRelativeS3Path,
} from '@/etc/aiElements'
import {
  groupPresetsByCategory,
  normalizeAiWorkflowPresets,
  normalizeWorkflowPreset,
} from '@/etc/aiWorkflowPresets'
import {
  cameraProfilePresetById,
  normalizeAiCameraProfilePresets,
  normalizeCameraProfilePreset,
} from '@/etc/aiCameraProfilePresets'
import {
  ANALYTICS_EVENTS,
  setAnalyticsAuth,
  trackAnalyticsEvent,
} from '@/etc/analytics'
const log = LOG.m('store::user')
const { VUE_APP_AI_BACKEND } = process.env
const AI_BACKEND_BASE = `${VUE_APP_AI_BACKEND || ''}`.trim().replace(/\/+$/, '')

function normalizeUserSettings(raw) {
  const base = raw && typeof raw === 'object' ? raw : {}
  const aiElements = normalizeAiElements(base.ai_elements)
  const aiWorkflowPresets = normalizeAiWorkflowPresets(base.ai_workflow_presets)
  const aiCameraProfilePresets = normalizeAiCameraProfilePresets(
    base.ai_camera_profile_presets,
  )

  return {
    ...base,
    ai_elements: aiElements,
    ai_workflow_presets: aiWorkflowPresets,
    ai_camera_profile_presets: aiCameraProfilePresets,
  }
}

// Sort index that lands a new element above every existing peer in the same
// kind + section, so freshly created elements surface at the top of their bucket.
function topSortIndexForSection(list, kind, section) {
  const peers = list.filter(
    (el) => el && el.kind === kind && `${el.section || ''}` === `${section || ''}`,
  )
  if (!peers.length) return 0
  return Math.min(...peers.map(getElementSortIndex)) - 1
}

function mergeAiElements(privateElements = [], ...globalLists) {
  const out = []
  const seen = new Set()
  ;[
    ...(Array.isArray(privateElements) ? privateElements : []),
    ...globalLists.flatMap((list) => (Array.isArray(list) ? list : [])),
  ].forEach((element) => {
    const id = `${element?.id || ''}`.trim()
    if (!id || seen.has(id)) return
    seen.add(id)
    out.push(element)
  })
  return out
}

export default {
  namespaced: true,
  state: {
    uid: null,
    jwt: null,
    metadata: null,
    profile: null,
    redirectAfterLogin: null,
    invite: null,
    messages: [],
    contacts: {},
    inChannel: null,
    settings: {},
    projects: {},
    wall: {},
  },
  getters: {
    isGuest: (store) => {
      return !!store.uid
    },
    unreadMessagesNum: (state) => {
      return state.messages.filter(
        (msg) => msg.sender !== state.uid && !msg.readed_at
      ).length
    },
    privateSettings: (state) => {
      return normalizeUserSettings(state.settings)
    },
    privateAiElements: (state, getters) => {
      return getters.privateSettings.ai_elements
    },
    aiElements: (state, getters, rootState) => {
      return mergeAiElements(
        getters.privateAiElements,
        rootState?.project?.globalPersonalityPresets,
        rootState?.project?.globalMoodPresets,
      )
    },
    aiElementsByKind: (state, getters) => {
      return groupElementsByKind(getters.aiElements)
    },
    aiElementById: (state, getters) => (id) => {
      const idRaw = `${id || ''}`.trim()
      if (!idRaw) return null
      return getters.aiElements.find((el) => el.id === idRaw) || null
    },
    aiWorkflowPresets: (state, getters) => {
      return getters.privateSettings.ai_workflow_presets
    },
    aiWorkflowPresetsByCategory: (state, getters) => {
      return groupPresetsByCategory(getters.aiWorkflowPresets)
    },
    aiWorkflowPresetById: (state, getters) => (id) => {
      const idRaw = `${id || ''}`.trim()
      if (!idRaw) return null
      return getters.aiWorkflowPresets.find((p) => p.id === idRaw) || null
    },
    aiCameraProfilePresets: (state, getters) => {
      return getters.privateSettings.ai_camera_profile_presets
    },
    aiCameraProfilePresetById: (state, getters) => (id) => {
      return cameraProfilePresetById(getters.aiCameraProfilePresets, id)
    },
  },
  mutations: {
    uid: (state, next) => {
      state.uid = next
    },
    jwt: (state, next) => {
      state.jwt = next
    },
    profile: (state, next) => {
      state.profile = next
    },
    settings: (state, next) => {
      state.settings = normalizeUserSettings(next)
    },
    invite: (state, next) => {
      state.invite = next
    },
    metadata: (state, next) => {
      state.metadata = next
    },
    redirectAfterLogin: (state, next) => {
      state.redirectAfterLogin = next
    },
    inChannel: (state, next) => {
      state.inChannel = next
    },
    setContacts: (state, next) => {
      state.contacts = next
    },
    addContact: (state, next) => {
      state.contacts[next.id] = next
    },
    setMessages: (state, next) => {
      state.messages = next
    },
    pushToMessages: (state, next) => {
      state.messages.push(next)
    },
    wall: (state, { uid, type, list }) => {
      // console.log('commit wall', uid, type, list)
      if (!['posts', 'showroom', 'shares', 'projects'].includes(type)) return
      if (!state.wall[uid])
        state.wall[uid] = {
          projects: [],
          posts: [],
          showroom: [],
          shares: [],
        }
      state.wall[uid][type] = list
    },
    projects: (state, next = []) => {
      next.forEach(({ id }) => {
        state.projects[id] = { id }
      })
    },
    clearWall: (state) => {
      state.wall = {}
    },
    clearProjects: (state) => {
      state.projects = {}
    },
  },
  actions: {
    init: (store) => {
      return new Promise((resolve) => {
        let resolved = false
        supabase.auth.onAuthStateChange((event, session) => {
          // await on other Supabase function to complete
          setTimeout(async () => {
            log.info('authStateChanged', { event, session })
            if (!session) {
              setAnalyticsAuth(null)
              store.commit('uid', null)
              store.commit('jwt', null)
              store.commit('metadata', null)
              store.commit('clearWall', null)
              store.commit('clearProjects', null)
              store.dispatch('logout', null, { root: true })
              return
            }
            if (session) {
              setAnalyticsAuth(session)
              store.commit('uid', session.user.id)
              store.commit('jwt', session.access_token)
              store.commit('metadata', session.user.app_metadata)
              await store.dispatch('login', null, { root: true })
              if (event === 'SIGNED_IN') {
                void trackAnalyticsEvent(ANALYTICS_EVENTS.USER_SIGNED_IN, {
                  entityType: 'user',
                  entityId: session.user.id,
                  metadata: { auth_event: event },
                })
                // store.commit('metadata', session.user.user_metadata)
                if (store.state.invite) {
                  await supabase
                    .from('invites')
                    .update({ receiver_id: session.user.id })
                    .eq({ id: store.state.invite.id })
                  store.commit('invite', null)
                  await supabase.from('profiles').insert({
                    id: session.user.id,
                  })
                }
              }
              // await store.dispatch('loadProfile')
              // await store.dispatch('loadChats')
            }
            if (!resolved) {
              resolved = true
              log.info('init done')
              resolve()
            }
          }, 0)
        })
        setTimeout(() => {
          if (!resolved) {
            resolved = true
            log.info('init')
            resolve()
          }
        }, 1000)
      })
    },
    signUpWithEmail: async (store, { invite, email, password }) => {
      store.commit('ui/busy', true, { root: true })
      store.commit('invite', invite)
      const res = await supabase.auth.signUp({
        email,
        password,
      })
      store.commit('ui/busy', false, { root: true })
      if (res.error) store.commit('invite', null)
      log.info('email signup', res)
    },
    signInWithEmail: async (store, { email, password }) => {
      store.commit('ui/busy', true, { root: true })
      await supabase.auth.signInWithPassword({ email, password })
      store.commit('ui/busy', false, { root: true })
    },
    loadProfile: async (store, username = null) => {
      const { error, data } = username
        ? await supabase.from('profiles').select().eq('username', username)
        : await supabase.from('profiles').select().eq('id', store.state.uid)
      // console.log('profile', profile)
      if (error) {
        console.error('request profile error', username, error)
        return null
      }
      // not my
      if (username) {
        return data && data[0] ? data[0] : null
      }
      // my
      if (!data || !data[0]) {
        store.commit('profile', null)
        return null
      }
      store.commit('profile', data[0])
      await store.dispatch('loadPrivateProfile')
      await store.dispatch('loadWall', { uid: store.state.uid, type: 'posts' })
      await store.dispatch('loadWall', { uid: store.state.uid, type: 'shares' })
      const projectsDb = await supabase
        .from('projects')
        .select()
        .eq('owner', store.state.uid)
        .order('updatedat', { ascending: false })
      store.commit('projects', (projectsDb || {}).data || [])
      return data[0]
    },
    loadWall: async (store, { uid, type }) => {
      const types = {
        posts: 'files',
        projects: 'projects',
        showroom: 'projects',
        shares: 'files',
      }
      const orders = {
        posts: 'createdat',
        projects: 'updatedat',
        showroom: 'updatedat',
        shares: 'createdat',
      }
      const filters = {
        showroom: { key: 'published', value: true },
        posts: { key: 'hearted', value: true },
      }
      let q = supabase.from(types[type]).select().eq('owner', uid)
      if (filters[type]) q = q.eq(filters[type].key, filters[type].value)

      q = q.order(orders[type], { ascending: false }).limit(100)

      const wallDb = await q

      /*
      if (uid === store.state.uid)
        wallDb = await supabase
          .from(types[type] || 'files')
          .select()
          .eq('owner', uid)
          .order(orders[type], { ascending: true })
          .limit(100)
      else
        wallDb = await supabase
          .from(types[type] || 'files')
          .select()
          .eq('owner', uid)
          .eq(onwalls[type].key, true)
          .order(orders[type], { ascending: true })
          .limit(100)
      // */
      // console.log('wallDb', wallDb, uid, type)
      const list =
        wallDb && wallDb.data && wallDb.data.length ? wallDb.data : []
      store.commit('wall', { uid, type, list })
      return list
    },
    refreshMediaWalls: async (store, { uid = null } = {}) => {
      const targetUid = uid || store.state.uid
      if (!targetUid) return false

      await Promise.all([
        store.dispatch('loadWall', { uid: targetUid, type: 'posts' }),
        store.dispatch('loadWall', { uid: targetUid, type: 'shares' }),
      ])
      return true
    },
    setFileHearted: async (store, { id, hearted = null, uid = null } = {}) => {
      const fileId = `${id || ''}`.trim()
      if (!fileId) return false
      if (!store.state.uid) return false

      let nextHearted = hearted
      if (typeof nextHearted !== 'boolean') {
        const { data: currentFile, error: currentErr } = await supabase
          .from('files')
          .select('id, hearted')
          .eq('id', fileId)
          .eq('owner', store.state.uid)
          .maybeSingle()
        if (currentErr || !currentFile) {
          console.error('load file before heart toggle failed', currentErr)
          return false
        }
        nextHearted = !currentFile.hearted
      }

      const { error } = await supabase
        .from('files')
        .update({ hearted: nextHearted })
        .eq('id', fileId)
        .eq('owner', store.state.uid)
      if (error) {
        console.error('set file hearted failed', error)
        return false
      }

      await store.dispatch('refreshMediaWalls', { uid: uid || store.state.uid })
      return true
    },
    deleteFileForever: async (store, { id, uid = null } = {}) => {
      const fileId = `${id || ''}`.trim()
      if (!fileId) return false

      const socket = store.rootState.io?._io?.core
      if (!socket) {
        console.error('delete file forever failed - no core socket')
        return false
      }

      let reply = null
      try {
        reply = await socket.emitWithAck('files:deleteForever', { id: fileId })
      } catch (err) {
        console.error('delete file forever socket error', err)
        return false
      }

      if (!reply?.success) {
        console.error('delete file forever failed', reply)
        return false
      }

      await store.dispatch('refreshMediaWalls', { uid: uid || store.state.uid })
      return true
    },
    deleteFilesForever: async (store, { ids = [], uid = null } = {}) => {
      const fileIds = [...new Set(
        (Array.isArray(ids) ? ids : [])
          .map((id) => `${id || ''}`.trim())
          .filter(Boolean)
      )]
      if (!fileIds.length) return false

      const socket = store.rootState.io?._io?.core
      if (!socket) {
        console.error('delete files forever failed - no core socket')
        return false
      }

      let replies = []
      replies = await Promise.all(
        fileIds.map(async (id) => {
          try {
            return await socket.emitWithAck('files:deleteForever', { id })
          } catch (err) {
            console.error('delete file forever socket error', { id, err })
            return { success: false, error: 'SOCKET_ERROR' }
          }
        })
      )

      const successCount = replies.filter((reply) => reply?.success).length
      if (successCount) {
        await store.dispatch('refreshMediaWalls', { uid: uid || store.state.uid })
      }
      if (successCount !== fileIds.length) {
        console.error('delete files forever failed', { fileIds, replies })
        return false
      }
      return true
    },
    loadPrivateProfile: async (store) => {
      store.commit('settings', {})
      if (!store.state.uid) {
        console.warn('private profile load failed - no uid')
        return
      }
      const { data, error } = await supabase
        .from('user_settings')
        .select()
        .eq('id', store.state.uid)
        .maybeSingle()
      if (error) return
      store.commit('settings', data || { id: store.state.uid })
      // await store.dispatch('loadProjectsList')
    },
    /*
    loadProjectsList: async (store) => {
      const { data, error } = await supabase.from('projects')
        .select('id, name, updatedat')
        // .select()
        .eq('owner', store.state.uid)
      // .maybeSingle()
      console.log('projects', data, error)
      if (error || !data) return
      store.commit('projects', data)
    },
    */
    loadProfileCover: async (store, url) => {
      const img = await supabase.storage.from('profiles').download(url, 3600)
      console.warn('load@cover', url, img)
      if (img.data && !img.error) return URL.createObjectURL(img.data)
      return null
    },
    loadInvite: async (store, code) => {
      console.log('invite code to load: ', code)
      const { error, data } = await supabase
        .from('invites')
        .select('*')
        .eq('id', code)
        .single()
      if (error) {
        console.error('loading invite failed', error)
        return null
      }
      console.log('invite found', data)
      const invite = data
      if (!invite || invite.receiver_id) return null
      void trackAnalyticsEvent(ANALYTICS_EVENTS.INVITE_VIEWED, {
        entityType: 'invite',
        entityId: invite.id,
      })
      return invite
    },
    updateProfile: async (store, upd) => {
      const nextUpd = { ...(upd || {}) }
      if (typeof nextUpd.avatar === 'string') {
        nextUpd.avatar = toRelativeS3Path(nextUpd.avatar)
      }
      if (typeof nextUpd.cover === 'string') {
        nextUpd.cover = toRelativeS3Path(nextUpd.cover)
      }
      if (
        nextUpd.custom &&
        typeof nextUpd.custom === 'object' &&
        typeof nextUpd.custom.pageBg === 'string'
      ) {
        nextUpd.custom = {
          ...nextUpd.custom,
          pageBg: toRelativeS3Path(nextUpd.custom.pageBg),
        }
      }
      // let's check that username is unique (only when updating username)
      const nextUsername =
        typeof nextUpd?.username === 'string' ? nextUpd.username.trim() : ''
      if (nextUsername) {
        const usernameCheck = await supabase
          .from('profiles')
          .select('*')
          .eq('username', nextUsername)
        if (usernameCheck.data && usernameCheck.data[0]) {
          if (usernameCheck.data[0].id !== store.state.uid) {
            alert('Username busy')
            return false
          }
        }
      }
      if (!store.state.profile) {
        const { error } = await supabase
          .from('profiles')
          .insert({ ...nextUpd, id: store.state.uid })
        if (error) {
          log.err('updateProfile error', error)
          return false
        }
      } else {
        const { error } = await supabase
          .from('profiles')
          .update(nextUpd)
          .eq('id', store.state.uid)
        if (error) {
          log.err('updateProfile error', error)
          return false
        }
      }
      const { error, data } = await supabase
        .from('profiles')
        .select()
        .eq('id', store.state.uid)
      if (error) {
        log.err('load profile after update error', error)
      }
      if (data && data[0]) {
        store.commit('profile', data[0])
      }
      log.info('updateProfile result', data, store.state.profile)
      return true
    },
    setProfilePageBg: async (store, { path = null } = {}) => {
      const prev = store.state.profile || {}
      const prevCustom = prev.custom || {}
      const nextCustom = { ...prevCustom }

      if (path) nextCustom.pageBg = path
      else delete nextCustom.pageBg

      return store.dispatch('updateProfile', { custom: nextCustom })
    },
    setProfileCanvasLayout: async (store, { layout = null } = {}) => {
      const prevCustom = store.state.profile?.custom || {}
      const nextCustom = { ...prevCustom }

      if (layout) nextCustom.canvasLayout = layout
      else delete nextCustom.canvasLayout

      return store.dispatch('updateProfile', { custom: nextCustom })
    },
    updatePrivateProfile: async (store, upd) => {
      if (!store.state.uid) return false

      const nextUpd = { ...(upd || {}) }
      if (Array.isArray(nextUpd.ai_elements)) {
        nextUpd.ai_elements = normalizeAiElements(nextUpd.ai_elements)
      }
      if (Array.isArray(nextUpd.ai_workflow_presets)) {
        nextUpd.ai_workflow_presets = normalizeAiWorkflowPresets(
          nextUpd.ai_workflow_presets,
        )
      }
      if (Array.isArray(nextUpd.ai_camera_profile_presets)) {
        nextUpd.ai_camera_profile_presets = normalizeAiCameraProfilePresets(
          nextUpd.ai_camera_profile_presets,
        )
      }

      const next = {
        ...(store.state.settings || {}),
        ...nextUpd,
        id: store.state.uid,
      }
      const normalizedNext = normalizeUserSettings(next)
      const { error } = await supabase
        .from('user_settings')
        .upsert({ ...normalizedNext })
      if (error) {
        log.err('updatePrivateProfile error', error)
        return false
      }
      store.commit('settings', normalizedNext)
      return true
    },
    saveAiElement: async (store, { element } = {}) => {
      const list = [...store.getters.privateAiElements]
      const idRaw = `${element?.id || ''}`.trim()
      const idx = idRaw ? list.findIndex((item) => item.id === idRaw) : -1

      const normalized = normalizeElement(element, idx >= 0 ? idx : list.length)
      if (!normalized) return false

      if (idx >= 0) {
        // Placement fields aren't part of every save payload (the debug helper
        // and older call sites omit them), so carry the stored values forward
        // rather than letting normalization reset them to defaults. Copied
        // verbatim: re-normalizing would flatten values belonging to a kind
        // this bundle doesn't know about.
        const prev = list[idx] || {}
        if (element.section === undefined && prev.section !== undefined) {
          normalized.section = prev.section
        }
        if (element.sortIndex === undefined && prev.sortIndex !== undefined) {
          normalized.sortIndex = prev.sortIndex
        }
        if (element.projectId === undefined && prev.projectId !== undefined) {
          normalized.projectId = prev.projectId
        }
        list.splice(idx, 1, normalized)
      } else {
        if (element.sortIndex === undefined) {
          normalized.sortIndex = topSortIndexForSection(
            list,
            normalized.kind,
            normalized.section,
          )
        }
        list.unshift(normalized)
      }

      return store.dispatch('updatePrivateProfile', {
        ai_elements: list,
      })
    },
    // Persists an entire drag in one upsert. Every element write rewrites the
    // whole user_settings row, so dispatching per tile would be a write storm.
    // `ordered` is the visible scope as [{ id, section }] in final visual order.
    // Hidden project elements retain their relative order and are placed before
    // the reordered scope, preventing a project-only drag from flattening other
    // projects' ordering.
    reorderAiElements: async (store, { kind, ordered } = {}) => {
      const kindRaw = `${kind || ''}`.trim().toLowerCase()
      const entries = Array.isArray(ordered) ? ordered : []
      if (!kindRaw || !entries.length) return false

      const desired = new Map()
      entries.forEach((entry) => {
        const id = `${entry?.id || ''}`.trim()
        if (!id || desired.has(id)) return
        desired.set(id, {
          id,
          section: normalizeElementSection(kindRaw, entry?.section),
        })
      })
      if (!desired.size) return false

      const source = store.getters.privateAiElements
      const byId = new Map(source.map((element) => [element.id, element]))
      const nextById = new Map(source.map((element) => [element.id, element]))
      const sections = getElementSections(kindRaw)

      sections.forEach((section) => {
        const hidden = source
          .filter(
            (element) =>
              element?.kind === kindRaw &&
              !desired.has(element.id) &&
              normalizeElementSection(kindRaw, element.section) === section.id,
          )
          .sort((a, b) => getElementSortIndex(a) - getElementSortIndex(b))
        const visible = entries
          .map((entry) => desired.get(`${entry?.id || ''}`.trim()))
          .filter((entry) => entry?.section === section.id && byId.has(entry.id))
          .map((entry) => ({
            ...byId.get(entry.id),
            section: section.id,
          }))

        ;[...hidden, ...visible].forEach((element, sortIndex) => {
          nextById.set(element.id, { ...element, sortIndex })
        })
      })

      const next = source.map((element) => nextById.get(element.id) || element)
      return store.dispatch('updatePrivateProfile', { ai_elements: next })
    },
    deleteAiElement: async (store, elementId) => {
      const idRaw = `${elementId || ''}`.trim()
      if (!idRaw) return false

      const source = store.getters.privateAiElements
      const removed = source.find((item) => item.id === idRaw)
      // Deleting a character would otherwise leave props and wardrobe pointing
      // at an id that no longer exists. The read side already ignores unresolved
      // links, so this is cleanup rather than correctness.
      const clearOwnerLinks = removed?.kind === 'character'
      const nextList = source
        .filter((item) => item.id !== idRaw)
        .map((item) => {
          if (!clearOwnerLinks) return item
          if (!kindSupportsOwnerLink(item?.kind)) return item
          if (`${item?.attributes?.belongsTo || ''}`.trim() !== idRaw) return item
          return {
            ...item,
            attributes: { ...(item.attributes || {}), belongsTo: '' },
          }
        })
      return store.dispatch('updatePrivateProfile', {
        ai_elements: nextList,
      })
    },
    saveAiWorkflowPreset: async (store, { preset } = {}) => {
      const normalized = normalizeWorkflowPreset(
        preset,
        store.getters.aiWorkflowPresets.length,
      )
      if (!normalized) return false

      const list = [...store.getters.aiWorkflowPresets]
      const idx = list.findIndex((item) => item.id === normalized.id)
      // Bump updatedAt on overwrite so the dropdown's "recent" sort order
      // reflects the latest edit. createdAt is preserved across overwrites.
      if (idx >= 0) {
        const prev = list[idx]
        normalized.createdAt = prev?.createdAt || normalized.createdAt
        normalized.updatedAt = new Date().toISOString()
        list.splice(idx, 1, normalized)
      } else {
        list.unshift(normalized)
      }

      return store.dispatch('updatePrivateProfile', {
        ai_workflow_presets: list,
      })
    },
    deleteAiWorkflowPreset: async (store, presetId) => {
      const idRaw = `${presetId || ''}`.trim()
      if (!idRaw) return false

      const nextList = store.getters.aiWorkflowPresets.filter(
        (item) => item.id !== idRaw,
      )
      return store.dispatch('updatePrivateProfile', {
        ai_workflow_presets: nextList,
      })
    },
    saveAiCameraProfilePreset: async (store, { preset } = {}) => {
      const normalized = normalizeCameraProfilePreset(preset)
      if (!normalized) return null
      const list = [...store.getters.aiCameraProfilePresets]
      const index = list.findIndex((item) => item.id === normalized.id)
      if (index >= 0) {
        const previous = list[index]
        normalized.createdAt =
          previous?.createdAt || normalized.createdAt
        normalized.updatedAt = new Date().toISOString()
        list.splice(index, 1, normalized)
      } else {
        list.unshift(normalized)
      }
      const saved = await store.dispatch('updatePrivateProfile', {
        ai_camera_profile_presets: list,
      })
      return saved ? normalized : null
    },
    deleteAiCameraProfilePreset: async (store, presetId) => {
      const id = `${presetId || ''}`.trim()
      if (!id) return false
      const next = store.getters.aiCameraProfilePresets.filter(
        (preset) => preset.id !== id,
      )
      return store.dispatch('updatePrivateProfile', {
        ai_camera_profile_presets: next,
      })
    },
    uploadAiElementImage: async (
      store,
      { elementId, file, role = 'ref', index = 0 } = {},
    ) => {
      const idRaw = `${elementId || ''}`.trim()
      if (!store.state.uid || !idRaw || !file) return null

      const roleRaw = `${role || 'ref'}`.trim().toLowerCase()
      const roleSafe = roleRaw === 'thumbnail' ? 'thumbnail' : 'ref'
      const idxSafe = Number.isFinite(Number(index)) ? Math.max(0, Math.floor(Number(index))) : 0
      const ext = inferFileExtension(file)
      const unique =
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
      const name = `${store.state.uid}-element-${idRaw}-${roleSafe}-${idxSafe}-${unique}.${ext}`
      const path = await store.dispatch('uploadFile', { file, name })
      return path ? toRelativeS3Path(path) : null
    },
    uploadFile: async (store, { file, name }) => {
      if (!AI_BACKEND_BASE) throw new Error('Backend URL is not configured')
      const jwt = `${store.state.jwt || ''}`.trim()
      if (!jwt) throw new Error('Authentication is required')
      const uploadName = `${name || ''}`.trim()
      if (!uploadName) throw new Error('Upload path is required')
      const blob = new Blob([file], { type: file.type })
      const body = new FormData()
      // The backend streams as soon as the file part starts, so send the
      // validated destination field first.
      body.append('to', uploadName)
      body.append('file', blob, uploadName.split('/').pop())
      const response = await fetch(`${AI_BACKEND_BASE}/vbapi/s3/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${jwt}` },
        body,
      })
      let upload = null
      try {
        upload = await response.json()
      } catch {
        throw new Error(`Upload failed (${response.status})`)
      }
      if (!response.ok || !upload?.success || !upload?.path) {
        throw new Error(upload?.error || `Upload failed (${response.status})`)
      }
      return upload.path
    },
    uploadAvatar: async (store, file) => {
      const ext = file.name.split('.').pop()
      const name = store.state.uid + '-avatar.' + ext
      const path = await store.dispatch('uploadFile', { file, name })
      if (!path) return null
      await store.dispatch('updateProfile', { avatar: path })
    },
    uploadBaner: async (store, file) => {
      const ext = file.name.split('.').pop()
      const name = store.state.uid + '-baner.' + ext
      const path = await store.dispatch('uploadFile', { file, name })
      if (!path) return null
      await store.dispatch('updateProfile', { cover: path })
    },
    loadChats: async (store) => {
      if (store.state.inChannel) return
      //if (store.state.messages.length)
      //return
      const { error, data } = await supabase
        .from('messages')
        .select()
        .order('created_at', { ascending: true })
      if (error) {
        console.error('loading chat messeges error', error)
        return
      }
      const msgs = data || []
      const contacts = []
      msgs.forEach((msg) => {
        const to =
          msg.chat_a === store.state.profile.id ? msg.chat_b : msg.chat_a
        if (!contacts.includes(to)) contacts.push(to)
      })
      const chatProfiles = await supabase
        .from('profiles')
        .select('*')
        .in('id', contacts)
      const chats = (chatProfiles.data || []).reduce((acc, p) => {
        acc[p.id] = p
        return acc
      }, {})
      //console.log('chats', chats, chatProfiles)
      store.commit('setMessages', msgs)
      store.commit('setContacts', chats)
      /*
      supabase
        .channel(`to-${store.state.profile.id}`)
        .on('broadcast', e => {
          console.log('to-me', e)
        })
        .subscribe()
      */
      if (!store.state.inChannel) {
        const inChannel = supabase
          .channel(`to-${store.state.profile.id}`)
          .on('broadcast', (e) => {
            console.log('to-me', e)
          })
          .subscribe()
        /*
        .channel('schema-db-changes')
        .on('postgres_changes', {
          event: '*',
          schema: 'public',
          table: 'messages',
          filter: `or(chat_a=eq.${store.state.profile.id},chat_b=eq.${store.state.profile.id}`
        }, payload => {
          log.info('Change received!', payload)
        })
      inChannel.on('postgres_changes', (pl) => {
        log.info('Change received2!', pl)
      })
      */
        console.log('inChannel', inChannel)
        store.commit('inChannel', inChannel)
      }
    },
    sendChat: async (store, data) => {
      const myId = store.state.profile.id
      const msg = await supabase
        .from('messages')
        .insert({
          chat_a: myId,
          chat_b: data.to,
          message: data.msg,
          sender: myId,
        })
        .select()
      if (msg && msg.data && msg.data[0]) {
        store.commit('pushToMessages', msg.data[0])
      }
      console.log('sendChat msg', msg)
      const channel = supabase.channel(`to-${data.to}`).subscribe((status) => {
        if (status !== 'subscribed') return
        channel.send({
          type: 'broadcast',
          event: 'chat',
          payload: { chat: myId },
        })
        supabase.removeChannel(channel)
      })
    },
    msgsMarkRead: async (store, id) => {
      log.info('markRead', id)
      const unread = store.state.messages.filter(
        (msg) => msg.sender === id && !msg.readed_at
      )
      if (!unread.length) {
        log.success('all readed')
        return
      }
      log.info('mark', unread.length, 'messages', unread)
      const msgs = unread.map((m) => m.id)
      const now = new Date()
      await supabase.from('messages').update({ readed_at: now }).in('id', msgs)
      log.success('mark ed')
    },
  },
}
